import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Trash2, Eye, Edit, Download, Plus } from 'lucide-react';
import { purchasesApi, suppliersApi, coldStoragesApi, brandsApi } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';
import AdvancedSearch, { DateRangeFilter, SelectFilter } from '../../components/AdvancedSearch';

interface Purchase {
  id: string;
  internalLot: string;
  purchaseDate: string;
  supplierId: string;
  productName: string;
  brandId?: string;
  brandName?: string;
  state: string;
  quantity: number;
  weight: string;
  unitPrice: string;
  totalAmount?: string;
  coldStorageId: string;
  coldStorageLot?: string;
  expiryDate?: string;
  storageFeeLimit?: string;
  remarks?: string;
}

export default function PurchaseList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState<any>(null);
  const [viewingPurchase, setViewingPurchase] = useState<any>(null);
  
  // メイン日付フィルタ（仕入日）
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // 詳細検索フィルタ
  const [advancedFilters, setAdvancedFilters] = useState({
    expiryDateFrom: '',
    expiryDateTo: '',
    state: '',
    supplierId: '',
    coldStorageId: '',
  });

  const [formData, setFormData] = useState({
    purchaseDate: new Date().toISOString().split('T')[0],
    supplierId: '',
    productName: '',
    brandId: '',
    brandName: '',
    state: 'チルド',
    quantity: '',
    weight: '',
    unitPrice: '',
    coldStorageId: '',
    coldStorageLot: '',
    expiryDate: '',
    storageFeeLimit: '',
    remarks: '',
  });

  // 詳細フィルタが有効かどうか
  const hasAdvancedFilters = Boolean(
    advancedFilters.expiryDateFrom || advancedFilters.expiryDateTo ||
    advancedFilters.state || advancedFilters.supplierId || advancedFilters.coldStorageId
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['purchases', search, dateFrom, dateTo, advancedFilters],
    queryFn: () => purchasesApi.getAll({ 
      search,
      purchaseDateFrom: dateFrom,
      purchaseDateTo: dateTo,
      expiryDateFrom: advancedFilters.expiryDateFrom,
      expiryDateTo: advancedFilters.expiryDateTo,
      state: advancedFilters.state,
      supplierId: advancedFilters.supplierId,
      coldStorageId: advancedFilters.coldStorageId,
    }),
    enabled: Boolean(dateFrom && dateTo), // 日付が設定されるまで待つ
  });

  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => suppliersApi.getAll({ active: 'true' }),
  });

  const { data: coldStoragesData } = useQuery({
    queryKey: ['cold-storages'],
    queryFn: () => coldStoragesApi.getAll({ active: 'true' }),
  });

  const { data: brandsData } = useQuery({
    queryKey: ['brands'],
    queryFn: () => brandsApi.getAll({ active: 'true' }),
  });

  const createMutation = useMutation({
    mutationFn: purchasesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      toast.success('仕入を登録しました');
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('登録に失敗しました'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => purchasesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      toast.success('仕入を更新しました');
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('更新に失敗しました'),
  });

  const deleteMutation = useMutation({
    mutationFn: purchasesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      toast.success('仕入を削除しました');
    },
    onError: () => toast.error('削除に失敗しました'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => purchasesApi.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      toast.success('選択した仕入を削除しました');
    },
    onError: () => toast.error('一括削除に失敗しました'),
  });

  const resetForm = () => {
    setFormData({
      purchaseDate: new Date().toISOString().split('T')[0],
      supplierId: '',
      productName: '',
      brandId: '',
      brandName: '',
      state: 'チルド',
      quantity: '',
      weight: '',
      unitPrice: '',
      coldStorageId: '',
      coldStorageLot: '',
      expiryDate: '',
      storageFeeLimit: '',
      remarks: '',
    });
    setEditingPurchase(null);
  };

  const handleExport = () => {
    const items = data?.data?.data || [];
    if (items.length === 0) {
      toast.error('エクスポートするデータがありません');
      return;
    }
    const headers = ['社内LOT', '仕入日', '仕入先', '商品名', 'ブランド', '状態', '数量', '重量', '単価', '金額', '冷蔵庫', '冷蔵庫LOT', '賞味期限'];
    const rows = items.map((item: any) => [
      item.purchase?.internalLot || '',
      item.purchase?.purchaseDate || '',
      item.supplier?.name || '',
      item.purchase?.productName || '',
      item.brand?.name || item.purchase?.brandName || '',
      item.purchase?.state || '',
      item.purchase?.quantity || '',
      item.purchase?.weight || '',
      item.purchase?.unitPrice || '',
      item.purchase?.totalAmount || '',
      item.coldStorage?.name || '',
      item.purchase?.coldStorageLot || '',
      item.purchase?.expiryDate || '',
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `仕入一覧_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('エクスポートしました');
  };

  const handleEdit = (row: any) => {
    const purchase = row.purchase;
    setEditingPurchase(row);
    setFormData({
      purchaseDate: purchase.purchaseDate || '',
      supplierId: purchase.supplierId || '',
      productName: purchase.productName || '',
      brandId: purchase.brandId || '',
      brandName: purchase.brandName || '',
      state: purchase.state || 'チルド',
      quantity: String(purchase.quantity || ''),
      weight: String(purchase.weight || ''),
      unitPrice: String(purchase.unitPrice || ''),
      coldStorageId: purchase.coldStorageId || '',
      coldStorageLot: purchase.coldStorageLot || '',
      expiryDate: purchase.expiryDate || '',
      storageFeeLimit: purchase.storageFeeLimit || '',
      remarks: purchase.remarks || '',
    });
    setIsModalOpen(true);
  };

  const handleView = (row: any) => {
    setViewingPurchase(row);
    setIsViewModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...formData,
      quantity: parseInt(formData.quantity),
      weight: formData.weight,
      unitPrice: formData.unitPrice,
    };

    if (editingPurchase) {
      updateMutation.mutate({ id: editingPurchase.purchase.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('この仕入を削除しますか？')) {
      deleteMutation.mutate(id);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (confirm(`${ids.length}件の仕入を削除しますか？`)) {
      bulkDeleteMutation.mutate(ids);
    }
  };

  const formatNumber = (num: any) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('ja-JP');
  };

  const columns = [
    { key: 'purchase.internalLot', label: '社内LOT', width: '100px' },
    { key: 'purchase.purchaseDate', label: '仕入日', width: '100px', sortable: true },
    { 
      key: 'supplier.name', 
      label: '仕入先',
      render: (row: any) => row.supplier?.name || '-',
    },
    { 
      key: 'purchase.productName', 
      label: '商品名',
      render: (row: any) => row.purchase?.productName || '-',
    },
    { 
      key: 'brand.name', 
      label: 'ブランド',
      render: (row: any) => row.brand?.name || row.purchase?.brandName || '-',
    },
    { 
      key: 'purchase.state', 
      label: '状態',
      width: '80px',
      render: (row: any) => (
        <span className={row.purchase?.state === 'チルド' ? 'badge badge-chilled' : 'badge badge-frozen'}>
          {row.purchase?.state || '-'}
        </span>
      ),
    },
    { 
      key: 'purchase.quantity', 
      label: '数量',
      width: '80px',
      render: (row: any) => formatNumber(row.purchase?.quantity),
    },
    { 
      key: 'purchase.weight', 
      label: '重量(kg)',
      width: '100px',
      render: (row: any) => formatNumber(row.purchase?.weight),
    },
    { 
      key: 'purchase.unitPrice', 
      label: '単価',
      width: '100px',
      render: (row: any) => `¥${formatNumber(row.purchase?.unitPrice)}`,
    },
    { 
      key: 'coldStorage.name', 
      label: '冷蔵庫',
      render: (row: any) => row.coldStorage?.name || '-',
    },
    {
      key: 'actions',
      label: '操作',
      width: '120px',
      render: (row: any) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleView(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--accent-color)' }}
            title="詳細表示"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--warning-color)' }}
            title="編集"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row.purchase.id); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--error-color)' }}
            title="削除"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  const bulkActions = [
    {
      label: '一括削除',
      icon: <Trash2 size={14} />,
      onClick: handleBulkDelete,
      variant: 'danger' as const,
    },
  ];

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title="仕入一覧"
        breadcrumbs={[{ label: '仕入管理' }]}
        // 日付フィルタ
        dateFilterKey="purchases-date-filter"
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
        // 検索
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="商品名で検索..."
        // 反映・詳細検索
        onApply={() => refetch()}
        onAdvancedSearch={() => setIsAdvancedSearchOpen(!isAdvancedSearchOpen)}
        hasAdvancedFilters={hasAdvancedFilters}
        // アクション
        actions={
          <div className="flex items-center gap-1.5">
            <button className="btn btn-outline btn-sm" onClick={handleExport}>
              <Download size={14} />
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setIsModalOpen(true); }}>
              <Plus size={14} />
              仕入登録
            </button>
          </div>
        }
      />

      {isAdvancedSearchOpen && (
        <AdvancedSearch
          dateFilters={[
            { field: 'expiryDate', label: '賞味期限', from: advancedFilters.expiryDateFrom, to: advancedFilters.expiryDateTo },
          ]}
          selectFilters={[
            { 
              field: 'state', 
              label: '状態', 
              value: advancedFilters.state, 
              options: [{ value: 'チルド', label: 'チルド' }, { value: '冷凍', label: '冷凍' }] 
            },
            {
              field: 'supplierId',
              label: '仕入先',
              value: advancedFilters.supplierId,
              options: (suppliersData?.data?.data || []).map((s: any) => ({ value: s.id, label: s.name })),
            },
            {
              field: 'coldStorageId',
              label: '冷蔵庫',
              value: advancedFilters.coldStorageId,
              options: (coldStoragesData?.data?.data || []).map((s: any) => ({ value: s.id, label: s.name })),
            },
          ]}
          onDateFilterChange={(field, type, value) => {
            const key = `${field}${type === 'from' ? 'From' : 'To'}`;
            setAdvancedFilters({ ...advancedFilters, [key]: value });
          }}
          onSelectFilterChange={(field, value) => {
            setAdvancedFilters({ ...advancedFilters, [field]: value });
          }}
          onClear={() => {
            setAdvancedFilters({ expiryDateFrom: '', expiryDateTo: '', state: '', supplierId: '', coldStorageId: '' });
          }}
          onSearch={() => refetch()}
        />
      )}

      <DataTable
        columns={columns}
        data={data?.data?.data || []}
        loading={isLoading}
        selectable
        bulkActions={bulkActions}
        getRowId={(row: any) => row.purchase?.id}
        emptyMessage="仕入データがありません"
      />

      {/* 詳細表示モーダル */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => { setIsViewModalOpen(false); setViewingPurchase(null); }}
        title="仕入詳細"
        size="lg"
      >
        {viewingPurchase && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">社内LOT</label>
                <p className="font-medium">{viewingPurchase.purchase?.internalLot || '-'}</p>
              </div>
              <div>
                <label className="label">仕入日</label>
                <p className="font-medium">{viewingPurchase.purchase?.purchaseDate || '-'}</p>
              </div>
              <div>
                <label className="label">仕入先</label>
                <p className="font-medium">{viewingPurchase.supplier?.name || '-'}</p>
              </div>
              <div>
                <label className="label">状態</label>
                <span className={viewingPurchase.purchase?.state === 'チルド' ? 'badge badge-chilled' : 'badge badge-frozen'}>
                  {viewingPurchase.purchase?.state || '-'}
                </span>
              </div>
              <div>
                <label className="label">商品名</label>
                <p className="font-medium">{viewingPurchase.purchase?.productName || '-'}</p>
              </div>
              <div>
                <label className="label">ブランド</label>
                <p className="font-medium">{viewingPurchase.brand?.name || viewingPurchase.purchase?.brandName || '-'}</p>
              </div>
              <div>
                <label className="label">数量</label>
                <p className="font-medium">{formatNumber(viewingPurchase.purchase?.quantity)} ケース</p>
              </div>
              <div>
                <label className="label">重量</label>
                <p className="font-medium">{formatNumber(viewingPurchase.purchase?.weight)} kg</p>
              </div>
              <div>
                <label className="label">単価</label>
                <p className="font-medium">¥{formatNumber(viewingPurchase.purchase?.unitPrice)}</p>
              </div>
              <div>
                <label className="label">合計金額</label>
                <p className="font-medium">¥{formatNumber(viewingPurchase.purchase?.totalAmount)}</p>
              </div>
              <div>
                <label className="label">冷蔵庫</label>
                <p className="font-medium">{viewingPurchase.coldStorage?.name || '-'}</p>
              </div>
              <div>
                <label className="label">冷蔵庫LOT</label>
                <p className="font-medium">{viewingPurchase.purchase?.coldStorageLot || '-'}</p>
              </div>
              <div>
                <label className="label">賞味期限</label>
                <p className="font-medium">{viewingPurchase.purchase?.expiryDate || '-'}</p>
              </div>
              <div>
                <label className="label">保管料期限</label>
                <p className="font-medium">{viewingPurchase.purchase?.storageFeeLimit || '-'}</p>
              </div>
            </div>
            {viewingPurchase.purchase?.remarks && (
              <div>
                <label className="label">備考</label>
                <p className="font-medium">{viewingPurchase.purchase.remarks}</p>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setIsViewModalOpen(false); setViewingPurchase(null); }}
              >
                閉じる
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setIsViewModalOpen(false);
                  handleEdit(viewingPurchase);
                }}
              >
                編集
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* 登録・編集モーダル */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title={editingPurchase ? '仕入編集' : '仕入登録'}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">仕入日 <span className="text-red-500">*</span></label>
              <input
                type="date"
                className="input-field"
                value={formData.purchaseDate}
                onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label">仕入先 <span className="text-red-500">*</span></label>
              <select
                className="input-field"
                value={formData.supplierId}
                onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                required
              >
                <option value="">選択してください</option>
                {suppliersData?.data?.data?.map((supplier: any) => (
                  <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">状態 <span className="text-red-500">*</span></label>
              <select
                className="input-field"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                required
              >
                <option value="チルド">チルド</option>
                <option value="冷凍">冷凍</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">商品名 <span className="text-red-500">*</span></label>
              <input
                type="text"
                className="input-field"
                value={formData.productName}
                onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label">ブランド</label>
              <select
                className="input-field"
                value={formData.brandId}
                onChange={(e) => {
                  const brand = brandsData?.data?.data?.find((b: any) => b.id === e.target.value);
                  setFormData({ 
                    ...formData, 
                    brandId: e.target.value,
                    brandName: brand?.name || '',
                  });
                }}
              >
                <option value="">選択してください</option>
                {brandsData?.data?.data?.map((brand: any) => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">数量(ケース) <span className="text-red-500">*</span></label>
              <input
                type="number"
                className="input-field"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label">重量(kg) <span className="text-red-500">*</span></label>
              <input
                type="number"
                step="0.01"
                className="input-field"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label">仕入単価(円/kg) <span className="text-red-500">*</span></label>
              <input
                type="number"
                step="0.01"
                className="input-field"
                value={formData.unitPrice}
                onChange={(e) => setFormData({ ...formData, unitPrice: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">冷蔵庫 <span className="text-red-500">*</span></label>
              <select
                className="input-field"
                value={formData.coldStorageId}
                onChange={(e) => setFormData({ ...formData, coldStorageId: e.target.value })}
                required
              >
                <option value="">選択してください</option>
                {coldStoragesData?.data?.data?.map((storage: any) => (
                  <option key={storage.id} value={storage.id}>{storage.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">冷蔵庫ロット</label>
              <input
                type="text"
                className="input-field"
                value={formData.coldStorageLot}
                onChange={(e) => setFormData({ ...formData, coldStorageLot: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">賞味期限</label>
              <input
                type="date"
                className="input-field"
                value={formData.expiryDate}
                onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
              />
            </div>
            <div>
              <label className="label">保管料期限</label>
              <input
                type="date"
                className="input-field"
                value={formData.storageFeeLimit}
                onChange={(e) => setFormData({ ...formData, storageFeeLimit: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="label">備考</label>
            <textarea
              className="input-field"
              rows={2}
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => { setIsModalOpen(false); resetForm(); }}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingPurchase ? '更新' : '登録'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
